import { GRAY_800, GRAY_900 } from 'constants/styling/theme'
import React, { ReactNode, useState } from 'react'
import {
  TransformComponent,
  TransformWrapper,
  useControls,
  useTransformEffect
} from 'react-zoom-pan-pinch'

import AddIcon from '@mui/icons-material/Add'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import RemoveIcon from '@mui/icons-material/Remove'
import Stack from '@mui/material/Stack'
import { Typography } from '@mui/material'
import { WHITE } from 'constants/styling/upbrandingTheme'

const ZoomScales = [25, 50, 75, 100, 125, 150, 175, 200, 250]

interface Props {
  /** The image to be displayed inside the ZoomPanel */
  children?: ReactNode
  /** Initial scale of the image when the ZoomPanel is rendered. */
  initialScale?: number
  /** Whether panning is disabled. If true, users cannot pan the image. 
   * This is custom set for purposes when we need to use image dragging for different actions */
  isPanningDisabled?: boolean
  /** Optional left toolbar component, can be used to add custom controls or buttons */
  toolBarComponent?: ReactNode
}

/**
 * @component
 * Image zoom panel component that allows users to zoom in and out of an image.
 *
 * @example
 * <ZoomPanel
 *   children={<img src="path/to/image.jpg" alt="Zoomable content" />}
 * />
 */
export const ZoomPanel: React.FC<Props> = ({
  children,
  initialScale = 0.75,
  isPanningDisabled = false,
  toolBarComponent = null
}) => {

  return (
    <TransformWrapper
      initialScale={initialScale}
      initialPositionX={0}
      initialPositionY={0}
      minScale={0.25}
      centerZoomedOut={true}
      centerOnInit={true}
      limitToBounds={false}
      panning={{ disabled: isPanningDisabled }}
      doubleClick={{ disabled: isPanningDisabled }}
      disablePadding={isPanningDisabled}
    >

      <ZoomControls toolBarComponent={toolBarComponent} />

      <TransformComponent
        wrapperStyle={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        contentStyle={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Stack alignItems="center" justifyContent="center" width="100%" height="100%" onClick={() => console.log('innnndndnd')}>
          {children}
        </Stack>
      </TransformComponent>

    </TransformWrapper >
  )
}

const ZoomControls = ({ toolBarComponent }: { toolBarComponent?: ReactNode }) => {
  const { zoomIn, zoomOut, setTransform, centerView } = useControls()

  const [zoomScales, setZoomScales] = useState<number>(75)
  const [transformPosition, setTransformPosition] = useState({ x: 0, y: 0 })

  useTransformEffect(({ state }) => {

    setZoomScales(Math.round(state.scale * 100))
    setTransformPosition({ x: state.positionX, y: state.positionY })
  })

  return (
    <Stack flexDirection="row" justifyContent="center" alignItems="center" margin="2.4rem 0">

      {/* Optional left toolbar component */}
      {toolBarComponent && (
        <Stack marginRight="1.6rem">
          {toolBarComponent}
        </Stack>
      )}

      {/* Zoom controls */}
      <Stack flexDirection="row" height="3.2rem" alignItems="center" borderRadius="3rem" padding=".6rem 1.6rem" border={`1px solid ${GRAY_900}`}>

        <Stack onClick={() => zoomOut()} sx={{ cursor: 'pointer' }}>
          <RemoveIcon sx={{ color: WHITE }} />
        </Stack>

        <MUIDivider orientation="vertical" margin={12} color={GRAY_800} />

        <MUIDropdown
          isDarkMode
          placement="bottom"
          button={(isOpen, action) => (
            <Stack onClick={action} color={WHITE} sx={{ cursor: 'pointer' }}>
              {zoomScales}%
            </Stack>
          )}
          sx={{ paddingTop: '.6rem' }}
        >
          {ZoomScales.map(scale => {
            return (
              <MUIDropdownItem
                key={scale}
                onClick={() => {
                  setZoomScales(scale)
                  setTransform(transformPosition.x, transformPosition.y, scale / 100, 300)
                  setTimeout(() => centerView(), 400)
                }}
                sx={{
                  backgroundColor: GRAY_900,
                  color: WHITE,
                  '&:hover': {
                    backgroundColor: GRAY_800,
                  },
                }}
              >
                <Typography variant="text-xs" fontWeight={400}>
                  {scale}%
                </Typography>
              </MUIDropdownItem>
            )
          })}
        </MUIDropdown>

        <MUIDivider orientation="vertical" margin={12} color={GRAY_800} />

        <Stack onClick={() => zoomIn()} sx={{ cursor: 'pointer' }}>
          <AddIcon sx={{ color: WHITE }} />
        </Stack>

      </Stack>

    </Stack>
  )
}
